import * as ReactDOM from 'react-dom';
import * as React from 'react';
import { DatasetAdapter, DatasetControl, IDatasetControl, Grid as GridBaseControl } from "@talxis/base-controls";
import { IInputs, IOutputs } from '.';

export class Grid implements ComponentFramework.StandardControl<IInputs, IOutputs> {
    private _container: HTMLDivElement;
    private _adapter: DatasetAdapter = new DatasetAdapter();
    private _state: ComponentFramework.Dictionary = {};

    /**
     * Used to initialize the control instance.
     * @param context The entire property bag available to control via Context Object.
     * @param notifyOutputChanged A callback method to alert the framework that the control has new outputs.
     * @param state A piece of data that persists in one session for a single user.
     * @param container The div element within which the control can render its content.
     */
    public init(context: ComponentFramework.Context<IInputs, IOutputs>, notifyOutputChanged: () => void, state: ComponentFramework.Dictionary, container: HTMLDivElement): void {
        this._container = container;
        this._state = state;
        this._adapter.init(context, notifyOutputChanged, container, {
            onGetDataset: () => context.parameters.Grid,
            onGetColumns: () => context.parameters.Columns?.raw ?? '[]',
            onGetHeight: () => context.parameters.Height?.raw,
            onGetHomePageGridClientApiRibbonButtonId: () => context.parameters.HomePageGridClientApiRibbonButtonId?.raw,
            onGetInlineRibbonButtonIds: () => context.parameters.InlineRibbonButtonIds?.raw
        });
    }

    /**
     * Called when any value in the property bag has changed.
     * @param context The entire property bag available to control via Context Object.
     */
    public updateView(context: ComponentFramework.Context<IInputs, IOutputs>): void {
        this._adapter.updateView(context);
        ReactDOM.render(React.createElement(DatasetControl, {
            context: <any>context,
            state: this._state,
            onGetControlComponent: (props) => React.createElement(GridBaseControl, props),
            parameters: {
                Grid: this._adapter.getDataset(),
                EnableEditing: {
                    raw: context.parameters.EnableEditing?.raw === 'true'
                },
                EnablePagination: {
                    raw: context.parameters.EnablePagination?.raw === 'false' ? false : true
                },
                EnableFiltering: {
                    raw: context.parameters.EnableFiltering?.raw === 'false' ? false : true
                },
                EnableSorting: {
                    raw: context.parameters.EnableSorting?.raw === 'false' ? false : true
                },
                EnableNavigation: {
                    raw: context.parameters.EnableNavigation?.raw === 'false' ? false : true
                },
                EnableOptionSetColors: {
                    raw: context.parameters.EnableOptionSetColors?.raw === 'true'
                },
                EnableAggregation: {
                    raw: context.parameters.EnableAggregation?.raw === 'true'
                },
                SelectableRows: this._getSelectableRowsParameter(context.parameters.SelectableRows),
                RowHeight: {
                    raw: context.parameters.RowHeight?.raw
                },
                EnableQuickFind: {
                    raw: false
                },
                Height: {
                    raw: this._adapter.getHeight()
                },
                InlineRibbonButtonIds: {
                    raw: context.parameters.InlineRibbonButtonIds?.raw
                }
            }
        } as IDatasetControl), this._container);
    }

    /**
     * Called to retrieve the output values from the control.
     * @returns The output object containing the dataset control.
     */
    public getOutputs(): IOutputs {
        return this._adapter.getOutputs();
    }

    /**
     * Called when the control is to be removed from the DOM.
     */
    public destroy(): void {
        ReactDOM.unmountComponentAtNode(this._container);
        this._adapter.destroy();
    }

    private _getSelectableRowsParameter(selectableRows?: ComponentFramework.PropertyTypes.EnumProperty<"none" | "single" | "multiple">) {
        switch (selectableRows?.raw) {
            //@ts-ignore - legacy binding support
            case 'true': {
                return {
                    ...selectableRows,
                    raw: 'multiple'
                };
            }
            //@ts-ignore - legacy binding support
            case 'false': {
                return {
                    ...selectableRows,
                    raw: 'none'
                };
            }
        }
        return selectableRows;
    }
}